import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { demoStudents, demoPartners, demoAdminPlacements, demoWorkLogs, demoPlacementSlots } from "@/data/demoData";
import { unwrapApiArray, unwrapApiObject } from "@/lib/api-response";
import { launchImpersonation } from "@/lib/impersonation";
import { toast } from "sonner";

/**
 * Opens a short-lived session as the given student or partner in a new tab, so
 * an admin can see the portal from their side. The API mints a 15 minute token
 * and records the action in the audit log; this hook only relays it to the tab.
 */
export function useImpersonateUser() {
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (target: { userId: number | string | null; name: string }) => {
      if (user?.isDemo) {
        throw new Error("Impersonation is unavailable in demo mode.");
      }
      if (target.userId === null || target.userId === undefined) {
        throw new Error("This record is not linked to an account.");
      }
      const res = await apiClient.post(`/admin/users/${target.userId}/impersonate`);
      return unwrapApiObject(res, null as any);
    },
    onSuccess: (data: any, target) => {
      if (!data?.token) {
        toast.error("Could not start that session.");
        return;
      }
      launchImpersonation(data.token, {
        role: data.role,
        name: data.name || target.name,
        email: data.email,
        userId: data.user_id,
        expiresIn: data.expires_in,
      });
      toast.success(`Opened ${data.role} portal`, {
        description: `Viewing as ${data.name || data.email}. This tab expires in 15 minutes.`,
      });
    },
    onError: (error: any) => {
      toast.error("Could not open that account", {
        description:
          error?.response?.data?.message ??
          error?.message ??
          "The account may be blocked, or already an administrator.",
      });
    },
  });
}

export function useAdminStudents() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-students"],
    queryFn: async () => {
      if (user?.isDemo) {
        return demoStudents;
      }
      try {
        const res = await apiClient.get("/admin/students");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useAdminPartners() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-partners"],
    queryFn: async () => {
      if (user?.isDemo) {
        return demoPartners;
      }
      try {
        const res = await apiClient.get("/admin/partners");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useAdminUnassigned() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-unassigned"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/admin/unassigned");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useAdminPlacements() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-placements"],
    queryFn: async () => {
      if (user?.isDemo) {
        return demoAdminPlacements;
      }
      try {
        const res = await apiClient.get("/admin/placements");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useAdminPlacementDetail(placementId?: string | number | null) {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-placement-detail", placementId],
    queryFn: async () => {
      if (user?.isDemo) {
        const match = demoAdminPlacements.find(
          (p) => String(p.id) === String(placementId),
        );
        return match ? { ...match, workLogs: [] } : null;
      }
      try {
        const res = await apiClient.get(`/admin/placements/${placementId}`, {
          skipGlobalErrorHandler: true,
        } as any);
        return unwrapApiObject(res, null as any);
      } catch (error) {
        console.error("Failed to fetch admin placement detail:", error);
        return null;
      }
    },
    enabled: !!user && placementId !== undefined && placementId !== null,
    retry: false,
    staleTime: 30_000,
  });
}

export function useAdminPlacementSlots(partnerUserId?: string | number) {
  const { user } = useAuthStore();

  return useQuery({
    // partnerUserId is a users.id because placement_slots.partner_id stores one.
    queryKey: ["admin-placement-slots", partnerUserId],
    queryFn: async () => {
      if (user?.isDemo) {
        if (!partnerUserId) return demoPlacementSlots;
        const partner = demoPartners.find(
          (p) => String(p.user_id ?? p.id) === String(partnerUserId),
        );
        return partner
          ? demoPlacementSlots.filter((slot) => slot.partner_name === partner.name)
          : [];
      }
      try {
        const params = partnerUserId ? { partner_id: partnerUserId } : {};
        const res = await apiClient.get("/admin/placements/slots", {
          params,
          skipGlobalErrorHandler: true,
        } as any);
        return unwrapApiArray(res);
      } catch (error) {
        console.error("Failed to fetch admin placement slots:", error);
        return [];
      }
    },
    enabled: !!user,
    retry: false,
  });
}

export function useCreatePlacement() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      student_id: number;
      partner_id: number;
      slot_id: number;
      start_date: number;
      end_date?: number;
      hours_required: number;
    }) => {
      if (user?.isDemo) {
        return { id: Date.now(), ...data, status: "active", hoursLogged: 0 };
      }
      const res = await apiClient.post("/admin/placements/assignments", data);
      return res.data.data;
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["admin-placements"], (old: any[]) =>
          old ? [...old, result] : [result]
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-placements"] });
      }
    },
    onError: (error: any) => {
      // The API answers 409 when the student already holds a live placement.
      // Surface the server wording so the admin knows to end the current
      // placement instead of retrying the same assignment.
      toast.error("Failed to assign student", {
        description: error?.response?.data?.message ?? error?.message,
      });
    },
  });
}

export function useAdminWorkLogs() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-work-logs"],
    queryFn: async () => {
      if (user?.isDemo) {
        return demoWorkLogs;
      }
      try {
        const res = await apiClient.get("/admin/work-logs");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useAdminPayments() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-payments"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/admin/payments");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useAdminCourses() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-courses"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/courses");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useAdminReports(type?: string) {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-reports", type],
    queryFn: async () => {
      if (user?.isDemo) {
        return null;
      }
      try {
        const res = await apiClient.get("/admin/reports", { params: { type } });
        return unwrapApiObject(res, null as any);
      } catch {
        return null;
      }
    },
  });
}

export function useAdminUsers() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const [studentsRes, partnersRes] = await Promise.all([
          apiClient.get("/admin/students"),
          apiClient.get("/admin/partners"),
        ]);

        const students = unwrapApiArray<Record<string, unknown>>(studentsRes).map((s) => ({
          ...s,
          role: "Student",
          joined: s.registeredDate || s.created_at,
        }));

        const partners = unwrapApiArray<Record<string, unknown>>(partnersRes).map((p) => ({
          ...p,
          role: "Partner",
          joined: p.registeredDate || p.created_at,
        }));

        return [...students, ...partners];
      } catch {
        return [];
      }
    },
  });
}

export function useApproveStudent() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true, message: "Student approved (Demo Mode)" };
      }
      const res = await apiClient.post(`/admin/students/${id}/approve`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-students"], (old: any[]) =>
          old?.map((s: any) =>
            String(s.id) === String(id) ? { ...s, status: "active", verification_status: "verified" } : s
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-students"] });
      }
    },
  });
}

export function useRejectStudent() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true, message: "Student rejected (Demo Mode)" };
      }
      const res = await apiClient.post(`/admin/students/${id}/reject`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-students"], (old: any[]) =>
          old?.map((s: any) =>
            String(s.id) === String(id) ? { ...s, status: "rejected" } : s
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-students"] });
      }
    },
  });
}

export function useEnrollStudent() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      email: string;
      password: string;
      university?: string;
      course?: string;
    }) => {
      if (user?.isDemo) {
        return { ...data, id: Date.now(), status: "pending", verification_status: "pending_review", registeredDate: new Date().toISOString() };
      }
      const res = await apiClient.post("/admin/users", { ...data, role: "student" });
      return res.data.data;
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["admin-students"], (old: any[]) =>
          old ? [...old, result] : [result]
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-students"] });
        queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      }
    },
  });
}

export function useApprovePartner() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true, message: "Partner approved (Demo Mode)" };
      }
      const res = await apiClient.post(`/admin/partners/${id}/approve`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-partners"], (old: any[]) =>
          old?.map((p: any) =>
            String(p.id) === String(id) ? { ...p, status: "active" } : p
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
      }
    },
  });
}

export function useRejectPartner() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true, message: "Partner rejected (Demo Mode)" };
      }
      const res = await apiClient.post(`/admin/partners/${id}/reject`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-partners"], (old: any[]) =>
          old?.map((p: any) =>
            String(p.id) === String(id) ? { ...p, status: "rejected" } : p
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
      }
    },
  });
}

export function useUpdatePartner() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: { id: string | number; payload: Record<string, unknown> }) => {
      if (user?.isDemo) {
        return { ...data.payload, id: data.id };
      }
      const res = await apiClient.patch(`/admin/partners/${data.id}`, data.payload);
      return res.data.data;
    },
    onSuccess: (_result, variables) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-partners"], (old: any[]) =>
          old?.map((p: any) =>
            String(p.id) === String(variables.id) ? { ...p, ...variables.payload } : p
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
      }
    },
  });
}

export function useOnboardPartner() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (user?.isDemo) {
        return { ...data, id: Date.now(), status: "pending", verification_status: "pending_review" };
      }
      const res = await apiClient.post("/admin/partners/onboard", data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
    },
  });
}

export function useUpdateStudent() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: { id: string | number; payload: any }) => {
      if (user?.isDemo) {
        return { ...data.payload, id: data.id };
      }
      const res = await apiClient.patch(`/admin/students/${data.id}`, data.payload);
      return res.data.data;
    },
    onSuccess: (result, variables) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["admin-students"], (old: any[]) =>
          old?.map((s: any) =>
            String(s.id) === String(variables.id) ? { ...s, ...result } : s
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-students"] });
        queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      }
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      email: string;
      name: string;
      password: string;
      role: string;
    }) => {
      if (user?.isDemo) {
        return { ...data, id: Date.now(), status: "active", joined: new Date().toISOString().split("T")[0] };
      }
      const res = await apiClient.post("/admin/users", data);
      return res.data.data;
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["admin-users"], (old: any[]) =>
          old ? [...old, result] : [result]
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      }
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: { id: string | number; payload: Record<string, unknown> }) => {
      if (user?.isDemo) {
        return { ...data.payload, id: data.id };
      }
      const res = await apiClient.patch(`/admin/users/${data.id}`, data.payload);
      return res.data.data;
    },
    onSuccess: (result, variables) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["admin-users"], (old: any[]) =>
          old?.map((u: any) =>
            String(u.id) === String(variables.id) ? { ...u, ...result } : u
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      }
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true, message: "User deleted (Demo Mode)" };
      }
      const res = await apiClient.delete(`/admin/users/${id}`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-users"], (old: any[]) =>
          old?.filter((u: any) => String(u.id) !== String(id))
        );
      } else {
        // A user is also a row in the partner/student lists, and now lands in
        // the trash, so all of those views go stale on delete.
        [["admin-users"], ["admin-students"], ["admin-partners"], ["admin-trash"]].forEach(
          (queryKey) => queryClient.invalidateQueries({ queryKey }),
        );
      }
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true, message: "User deactivated (Demo Mode)" };
      }
      const res = await apiClient.post(`/admin/users/${id}/deactivate`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-users"], (old: any[]) =>
          old?.map((u: any) =>
            String(u.id) === String(id) ? { ...u, status: "inactive" } : u
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      }
    },
  });
}

export function useResetUserPassword() {
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { email: "demo@example.com", temporary_password: "Demo@123" };
      }
      const res = await apiClient.post(`/admin/users/${id}/reset-password`);
      return res.data.data;
    },
  });
}

export function useAdminSeminars() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-seminars"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [
          {
            id: "1",
            title: "Orientation for Engineering Interns",
            host: "Dr. Jane Mdoe",
            date: "15 May 2026",
            time: "09:00 AM - 11:00 AM",
            location: "Auditorium A, UDSM",
            attendees: 120,
            status: "upcoming",
            type: "on-site",
          },
          {
            id: "2",
            title: "Work Ethic & Professionalism Webinar",
            host: "John Msigwa",
            date: "18 May 2026",
            time: "02:00 PM - 03:30 PM",
            location: "Virtual Zoom Link",
            attendees: 250,
            status: "upcoming",
            type: "virtual",
          },
        ];
      }
      try {
        const res = await apiClient.get("/admin/seminars");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useCreateSeminar() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      title: string;
      host: string;
      date: string;
      time: string;
      location: string;
      type: string;
    }) => {
      if (user?.isDemo) {
        return { ...data, id: Date.now(), attendees: 0, status: "upcoming" };
      }
      const res = await apiClient.post("/admin/seminars", data);
      return res.data.data;
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["admin-seminars"], (old: any[]) =>
          old ? [...old, result] : [result]
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-seminars"] });
      }
    },
  });
}

export function useUpdateSeminar() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: { id: string | number; payload: Record<string, unknown> }) => {
      if (user?.isDemo) {
        return { ...data.payload, id: data.id };
      }
      const res = await apiClient.patch(`/admin/seminars/${data.id}`, data.payload);
      return res.data.data;
    },
    onSuccess: (result, variables) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["admin-seminars"], (old: any[]) =>
          old?.map((s: any) =>
            String(s.id) === String(variables.id) ? { ...s, ...result } : s
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-seminars"] });
      }
    },
  });
}

export function useDeleteSeminar() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true, message: "Seminar deleted (Demo Mode)" };
      }
      const res = await apiClient.delete(`/admin/seminars/${id}`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-seminars"], (old: any[]) =>
          old?.filter((s: any) => String(s.id) !== String(id))
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-seminars"] });
      }
    },
  });
}

// Certificate hooks
export function useAdminCertificates() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-certificates"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [
          { id: "1", name: "Field Attachment Completion", type: "completion", issued: 245, status: "active", border_color: "#1A56DB", accent_color: "#3B82F6", badge_icon: "award", description: "Awarded upon successful completion of field attachment hours." },
          { id: "2", name: "Industrial Training Participation", type: "participation", issued: 189, status: "active", border_color: "#059669", accent_color: "#10B981", badge_icon: "briefcase", description: "Issued to students who actively participated in industrial training." },
          { id: "3", name: "Academic Excellence Recognition", type: "excellence", issued: 34, status: "active", border_color: "#D97706", accent_color: "#F59E0B", badge_icon: "star", description: "Recognizes outstanding academic performance." },
          { id: "4", name: "Professional Skills Achievement", type: "achievement", issued: 12, status: "active", border_color: "#7C3AED", accent_color: "#8B5CF6", badge_icon: "target", description: "Granted for demonstrating exceptional professional skills." },
        ];
      }
      try {
        const res = await apiClient.get("/certificates/templates");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useCreateCertificateTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (user?.isDemo) {
        return { ...data, id: Date.now(), issued: 0 };
      }
      const res = await apiClient.post("/certificates/templates", data);
      return res.data.data;
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["admin-certificates"], (old: any[]) =>
          old ? [...old, result] : [result]
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
      }
    },
  });
}

export function useUpdateCertificateTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: { id: string | number; payload: Record<string, unknown> }) => {
      if (user?.isDemo) {
        return { ...data.payload, id: data.id };
      }
      const res = await apiClient.patch(`/certificates/templates/${data.id}`, data.payload);
      return res.data.data;
    },
    onSuccess: (result, variables) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["admin-certificates"], (old: any[]) =>
          old?.map((c: any) =>
            String(c.id) === String(variables.id) ? { ...c, ...result } : c
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
      }
    },
  });
}

export function useDeleteCertificateTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true };
      }
      const res = await apiClient.delete(`/certificates/templates/${id}`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-certificates"], (old: any[]) =>
          old?.filter((c: any) => String(c.id) !== String(id))
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
      }
    },
  });
}

export function useIssueCertificate() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      template_id: number;
      user_id: number;
      student_name: string;
      course: string;
      university: string;
    }) => {
      if (user?.isDemo) {
        return { ...data, id: Date.now(), cert_number: `DEMO-${Date.now()}`, issue_date: new Date().toISOString().split("T")[0] };
      }
      const res = await apiClient.post("/certificates/issue", data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
      queryClient.invalidateQueries({ queryKey: ["student-certificates"] });
    },
  });
}

export function useIssuedCertificates() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-issued-certificates"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/certificates/issued");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useRevokeCertificate() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true };
      }
      const res = await apiClient.post(`/certificates/${id}/revoke`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-issued-certificates"] });
      queryClient.invalidateQueries({ queryKey: ["student-certificates"] });
    },
  });
}

// Reference data hooks (industries & skills)
export function useIndustries() {
  return useQuery({
    queryKey: ["industries"],
    queryFn: async () => {
      try {
        const res = await apiClient.get("/reference/industries");
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useSkills(industryId?: string | number) {
  return useQuery({
    queryKey: ["skills", industryId],
    queryFn: async () => {
      try {
        const params = industryId ? { industry_id: industryId } : {};
        const res = await apiClient.get("/reference/skills", { params });
        return unwrapApiArray(res);
      } catch {
        return [];
      }
    },
  });
}

export function useDiscoverCourses(search: string, category: string) {
  return useQuery({
    queryKey: ["discover-courses", search, category],
    queryFn: async () => {
      try {
        const res = await apiClient.get("/admin/courses/discovery", {
          params: { search, category },
        });
        return unwrapApiArray(res);
      } catch (error) {
        console.error("Failed to discover courses:", error);
        return [];
      }
    },
  });
}

export function useImportCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      external_id: string;
      visibility: string;
      pricing_type: string;
      price: number;
      custom_description: string;
    }) => {
      const res = await apiClient.post("/admin/courses/import", data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-courses"] });
      queryClient.invalidateQueries({ queryKey: ["student-courses"] });
    },
  });
}

export function useDeleteCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true };
      }
      const res = await apiClient.delete(`/admin/courses/${id}`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-courses"], (old: any[]) =>
          old?.filter((c: any) => String(c.id) !== String(id))
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-courses"] });
        queryClient.invalidateQueries({ queryKey: ["student-courses"] });
      }
    },
  });
}

// ─── Trash (soft-deleted records) ───

export type TrashItem = {
  type: "user" | "placement";
  id: number;
  label: string;
  sublabel: string;
  role?: string;
  deleted_at: string;
};

export function useTrash() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-trash"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [] as TrashItem[];
      }
      const res = await apiClient.get("/admin/trash");
      return unwrapApiArray(res) as TrashItem[];
    },
  });
}

// Restoring or purging changes what the partner/student/placement lists
// contain, so those caches have to be invalidated alongside the trash itself.
const TRASH_AFFECTED_KEYS = [
  ["admin-trash"],
  ["admin-users"],
  ["admin-students"],
  ["admin-partners"],
  ["admin-placements"],
];

function useTrashMutation(
  request: (item: Pick<TrashItem, "type" | "id">) => Promise<unknown>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (item: Pick<TrashItem, "type" | "id">) => request(item),
    onSuccess: () => {
      TRASH_AFFECTED_KEYS.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
    },
  });
}

export function useRestoreTrashItem() {
  return useTrashMutation(async ({ type, id }) => {
    const res = await apiClient.post(`/admin/trash/${type}/${id}/restore`);
    return res.data.data;
  });
}

export function usePurgeTrashItem() {
  return useTrashMutation(async ({ type, id }) => {
    const res = await apiClient.delete(`/admin/trash/${type}/${id}`);
    return res.data.data;
  });
}

export function useAdminCreateSlot() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      partnerId: number;
      title: string;
      description?: string;
      requirements?: string;
      location?: string;
      duration?: string;
      arrangement?: string;
      max_students: number;
    }) => {
      if (user?.isDemo) {
        return { id: Date.now(), ...data, status: "published" };
      }
      const { partnerId, ...payload } = data;
      const res = await apiClient.post(`/admin/partners/${partnerId}/slots`, payload);
      return res.data.data;
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin-placement-slots"] });
      queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
    },
  });
}

export function useAdminUpdateSlot() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      id: number;
      title: string;
      description?: string;
      requirements?: string;
      location?: string;
      duration?: string;
      arrangement?: string;
      max_students: number;
      status?: string;
    }) => {
      if (user?.isDemo) {
        return data;
      }
      const { id, ...payload } = data;
      const res = await apiClient.patch(`/admin/slots/${id}`, payload);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-placement-slots"] });
      queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
    },
  });
}

export function useAdminDeleteSlot() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: number) => {
      if (user?.isDemo) {
        return { success: true };
      }
      const res = await apiClient.delete(`/admin/slots/${id}`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-placement-slots"] });
      queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
    },
  });
}

export function useDeletePlacement() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true, message: "Placement terminated (Demo Mode)" };
      }
      const res = await apiClient.delete(`/admin/placements/${id}`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["admin-placements"], (old: any[]) =>
          old?.filter((p: any) => String(p.id) !== String(id)),
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-placements"] });
        queryClient.invalidateQueries({ queryKey: ["admin-trash"] });
      }
    },
  });
}
